import {
  BackfillWarningDto,
  FillType,
  ThroughputControlType,
  UcsCoefficientsDto
} from 'providers/api';
import PCNode from './Nodes/PCNode';
import PCPipe from './Pipes/PCPipe';
import {
  GREEN,
  LIGHT_BLUE,
  LIGHT_RED,
  PURPLE,
  RED
} from './GConstants';
import type PCRoute from './Route/PCRoute';

export type StopePanelTab = 'recipes' | 'pours';

export interface StopeFillState {
  stopeId: string;
  projectId: string;
  geometryPath: string;
  drawerOpen: boolean;
  // The project's default paste beach slope in degrees from horizontal, or null/undefined to fall back
  // to the legacy 1.25. Passed as a prop rather than published to stopeFillSceneState so the fill walk
  // can never run against a stale or not-yet-published default.
  projectBeachAngleDegrees?: number | null;
}

export interface RecipeState {
  throughputControlType?: ThroughputControlType;
  dryDensity?: number;
  wetDensity?: number;
  dryTonnage?: number;
  wetTonnage?: number;
  dryFlowRate?: number;
  wetFlowRate?: number;
  tailingsDryTonnage?: number;
  binderConcentrationByMass?: number;
  totalDryConcentrationByMass?: number;
  pumpPressureKpa?: number;
  unconfinedCompressiveStrengthKpa?: number;
  curingDuration?: number;
  tailingsSolidsDensity?: number;
  binderParticleDensity?: number;
  carrierFluidDensity?: number;
  boosterPumps?: BoosterPumps[];
  recipeTab?: StopePanelTab;
  pipeRatingOffsetPercent?: number;
  rheologyDataSetId?: string;
}

export interface PipeJSON {
  name: string;
  pipeId: string;
  class: string;
  insideDiameter: number; // m
  pressureRating: number; // kPa
  wallThickness: number; // m
  material?: string;
}

export interface PointJSON {
  pointId: string,
  x: number,
  y: number,
  z: number
}

export type VelocityResult = {
  velocity: number;
  errors: string;
}

export type MinVelocityResult = {
  velocity: number;
  pipe: PCPipe;
}

export type BoosterPumps = {
  name: string;
  pressureLimitKpa: number;
  pressureKpa: number;
}

export interface RheologyCoefficients {
  a: number;
  b: number;
  c: number;
  d: number;
}

export interface PCFluidSpecification {
  version: string;
  type: FillType;
  concentrationRange: Range;
  tonnageRange: Range;
  binderRange: Range;
  drySolidsDensity: number; // kPa
  rheologyCoefficients: RheologyCoefficients;
  ucsCoefficients: Record<number, UcsCoefficientsDto>;
  maxPumpPressure?: number; // bar
  heightOfCylinder?: number; // m
  mixerCoefficients?: any;
  carrierFluidDensity?: number;
  binderDryParticleDensity?: number;
  frictionLossFactorOfSafety?: number;
}

export type Telemetry = [string, string][];

export interface RouteWarning extends BackfillWarningDto {
  color: string;
}

export interface RangeBase {
  min: number;
  max: number;
}

export interface Range extends RangeBase {
  interval: number;
  default?: number;
}

export interface DictionaryRange {
  concentration: Range;
  tons: Range;
  cement: Range;
  pumpPressure: Range;
}

export enum OptimisationTargetType {
  KPaPerM = 0,
  PumpPressure = 1,
  Yield = 2,
  FlowRate = 3,
}

export interface NominalOptimisationParams {
  minYield?: number;
  maxYield?: number;
  pipeRatingOffsetPercent?: number;
  excludedPipeIds?: string[];
}

export interface OptimisationTargetLineInput {
  targetType: OptimisationTargetType;
  kPaPerMTarget?: number;
  targetPipeId?: string;
  pumpPressureTarget?: number;
  yieldTarget?: number;
  flowRateTarget?: number; // m³/h
}

export interface OptimisationTargetLineResult extends OptimisationTargetLineInput {
  result?: PasteRecipe;
  hasErrors: boolean;
  errorDescription?: string;
}

export enum OptimisationErrorType {
  NONE = 0,
  UCS_TOO_HIGH = 5,
  UCS_TOO_LOW = 10,
  OTHER = 15,
}

export interface PasteOptimiseResult {
  optimisedResult: PasteRecipe;
  slackFlowResult?: PasteRecipe;
  maxPressureResult?: PasteRecipe;
  targetLineResults?: OptimisationTargetLineResult[];
}

export interface PasteRecipe {
  cement: number;
  concentrationByMass: number;
  actualUCSKpa: number;
  actualPumpPressureKpa: number;
  error: OptimisationErrorType;
  route: PCRoute;
  slurryDensity: number;
}

export interface PasteAdjustedResult {
  actualUCSKpa: number;
  actualPumpPressureKpa: number;
}

export interface HydraulicOptimiseResult {
  concentrationByMass: number;
}

export interface HydraulicCalculateResult {
  settlingVelocity: number;
  maxPipePressure: number;
  minVelocity: number;
}

export type RoutePoint = {
  point: PCNode,
  distanceFromPump?: number,
  pressure?: number,
  pipeUpstream?: PCPipe,
  pipeDownstream?: PCPipe,
  minPressure?: number,
  maxPressure?: number,
}

export const OVER_PRESSURE_WARNING: RouteWarning = {
  code: 'OP',
  display: 'Over Pressure',
  color: RED,
};

export const OVER_PRESSURE_POSSIBLE_WARNING: RouteWarning = {
  code: 'OPP',
  display: 'Over Pressure Possible',
  color: LIGHT_RED,
};

export const NO_WARNING: RouteWarning = {
  code: 'NW',
  display: 'No Warnings',
  color: GREEN,
};

export const SLACK_FLOW_POSSIBLE_WARNING: RouteWarning = {
  code: 'SFP',
  display: 'Slack Flow possible',
  color: LIGHT_BLUE,
};

export const SLACK_FLOW_WARNING: RouteWarning = {
  code: 'SF',
  display: 'Slack Flow',
  color: PURPLE,
};

export const WARNINGS = [
  OVER_PRESSURE_WARNING,
  OVER_PRESSURE_POSSIBLE_WARNING,
  NO_WARNING,
  SLACK_FLOW_POSSIBLE_WARNING,
  SLACK_FLOW_WARNING,
];
